import { lazyLoad } from 'utils/lazyLoad';

// ===================================================================
// PHASE 6: CODE SPLITTING - Public Routes Lazy Loading 🚀
// ===================================================================
// All public pages are now loaded on-demand to reduce initial bundle
// Using lazyLoad utility with automatic retry logic for reliability
// ===================================================================

// Lazy load all public pages with retry support
const Login = lazyLoad(() => import('pages/Login').then(module => ({ default: module.Login })));
const Register = lazyLoad(() =>
  import('pages/Register').then(module => ({ default: module.Register }))
);
const RegisterIframe = lazyLoad(() =>
  import('pages/RegisterIframe').then(module => ({ default: module.RegisterIframe }))
);
const ForgotPassword = lazyLoad(() =>
  import('pages/ForgotPassword').then(module => ({ default: module.ForgotPassword }))
);
const EventInvite = lazyLoad(() =>
  import('pages/EventInvite').then(module => ({ default: module.EventInvite }))
);
const Logout = lazyLoad(() => import('components/Logout'));
const EmbeddedFormViewer = lazyLoad(() =>
  import('pages/EmbeddedFormViewer').then(module => ({ default: module.EmbeddedFormViewer }))
);
const TestPhase0 = lazyLoad(() => import('pages/TestPhase0'));

interface IPublicRoute {
  documentId: string;
  component: React.ReactNode;
  path: string;
  isPublic: boolean;
}

// Base routes (production)
const baseRoutes: IPublicRoute[] = [
  {
    documentId: 'login',
    component: <Login />,
    path: '/login',
    isPublic: true,
  },
  {
    documentId: 'register',
    component: <Register />,
    path: '/register',
    isPublic: true,
  },
  {
    documentId: 'register-iframe',
    component: <RegisterIframe />,
    path: '/register-iframe',
    isPublic: true,
  },
  {
    documentId: 'forgot-password',
    component: <ForgotPassword />,
    path: '/forgot-password',
    isPublic: true,
  },
  {
    documentId: 'event-invite',
    component: <EventInvite />,
    path: '/invite/event/:invite_uuid',
    isPublic: true,
  },
  { documentId: 'logout', component: <Logout />, path: '/logout', isPublic: true },
  {
    documentId: 'embedded-form',
    component: <EmbeddedFormViewer />,
    path: '/form/:formGroupId',
    isPublic: true,
  },
];

// Development-only routes
const developmentRoutes: IPublicRoute[] = [
  {
    documentId: 'test-phase-0',
    component: <TestPhase0 />,
    path: '/test/phase-0',
    isPublic: true,
  },
];

// Export routes based on environment
export const PublicRoutes: IPublicRoute[] =
  process.env.NODE_ENV === 'development' ? [...baseRoutes, ...developmentRoutes] : baseRoutes;

export const isPublicRoute = (path: string): boolean => {
  // First try to match static exact paths
  if (PublicRoutes.some(route => route.path === path && route.isPublic)) {
    return true;
  }

  // Then test for dynamic params using a simple path-to-regexp approach
  for (const route of PublicRoutes) {
    if (!route.isPublic) continue;

    // Escape regex special chars EXCEPT for ":" (needed for param matching)
    const pattern = route.path
      .replace(/([.+?^=!${}()|[\]/\\])/g, '\\$1') // Removed : from escape pattern
      .replace(/:([^/]+)/g, '[^/]+'); // Replace :param with a dynamic part

    const regex = new RegExp(`^${pattern}$`);
    if (regex.test(path)) {
      return true;
    }
  }

  return false;
};
